import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SingleSelect } from '@/components/common/SingleSelect';
import { MultiSelect } from '@/components/common/MultiSelect';
import PaginatedGentlyTable, {
  TableColumn,
  type PaginationConfig,
} from '@/components/common/PaginatedGentlyTable';
import { TableSkeleton } from '@/components/common/loading-skeletons/table-skeleton';
import { useExportTable } from '@/hooks/useExportTable';
import {
  useSalesMarginsExport,
  downloadSalesMarginsCsv,
  deleteSalesMarginsForYear,
} from '@/api/rebates/useRebateDataExports';
import type { SalesMarginExportItem } from '@erp/shared-types';
import { formatCurrency } from '@erp/shared-utils';
import {
  DownloadIcon,
  MagnifyingGlassIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import useDivisions from '@/api/organization/useDivisions';
import { DatePicker } from '@/components/common/date-pickers/date-picker';
import useAllSuppliers from '@/api/accounts-payable/useAllSuppliersQuery';
import useCoreEnums from '@/api/core/core-enums/useCoreEnums';
import { REBATES_SYSTEM_ENUMS } from '@erp/shared-types';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { useToast } from '@/context/ToastContext';

const SalesMarginsExportTable: React.FC = () => {
  const [shouldFetchData, setShouldFetchData] = useState(false);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { showToast } = useToast();

  const exportTable = useExportTable({ initialPageSize: 100 });

  const { data: divisionsData } = useDivisions(true);
  const { data: allSuppliers } = useAllSuppliers();
  const { data: supplierTagsEnum } = useCoreEnums({
    name: REBATES_SYSTEM_ENUMS.SUPPLIER_TAGS.name,
    includeOptions: true,
  });

  // Extract year from date range for delete functionality
  const selectedYear = useMemo(() => {
    if (exportTable.filters.dateRange?.from) {
      return exportTable.filters.dateRange.from.getUTCFullYear();
    }
    return new Date().getUTCFullYear();
  }, [exportTable.filters.dateRange]);

  const divisionOptions = useMemo(() => {
    if (!divisionsData?.divisions) return [];
    return divisionsData.divisions.map((division) => ({
      id: division.id,
      label: division.name,
      value: division.id,
    }));
  }, [divisionsData]);

  const supplierOptions = useMemo(() => {
    if (!allSuppliers) return [];
    return allSuppliers.map((supplier) => ({
      id: supplier.id,
      label: supplier.name,
      value: supplier.id,
    }));
  }, [allSuppliers]);

  const supplierTagOptions = useMemo(() => {
    if (!supplierTagsEnum?.edges?.[0]?.options) return [];
    return supplierTagsEnum.edges[0].options.map((option) => ({
      id: option.id,
      label: option.name || '',
      value: option.id,
    }));
  }, [supplierTagsEnum]);

  const apiFilters = useMemo(
    () => ({ ...exportTable.buildApiFilters(), includeCodes: true }),
    [exportTable],
  );
  const { data, isLoading, refetch } = useSalesMarginsExport(
    apiFilters,
    shouldFetchData,
  );

  useEffect(() => {
    if (data?.pageInfo) {
      exportTable.pagination.setPaginationInfo({
        totalCount: data.pageInfo.totalCount,
        totalPages: data.pageInfo.totalPages,
        currentPage: data.pageInfo.currentPage,
      });
      exportTable.pagination.setHasNextPage(data.pageInfo.hasNextPage);
    }
  }, [data, exportTable.pagination]);

  const columns: TableColumn<SalesMarginExportItem>[] = useMemo(
    () => [
      { id: 'yearMonth', label: 'Year-Month', sortable: true },
      { id: 'supplierName', label: 'Supplier', sortable: true },
      {
        id: 'supplierCode',
        label: 'Supplier Code',
        sortable: true,
        render: (val) => (val ? String(val) : '-'),
      },
      { id: 'divisionName', label: 'Division', sortable: true },
      {
        id: 'divisionCode',
        label: 'Division Code',
        sortable: true,
        render: (val) => (val ? String(val) : '-'),
      },
      {
        id: 'productFamilyDesc',
        label: 'Product Family Desc',
        sortable: true,
        render: (val) => (val as string | null) || '-',
      },
      {
        id: 'shipType',
        label: 'Ship Type',
        sortable: true,
        render: (val) => (val !== null ? String(val) : '-'),
      },
      {
        id: 'salesCostLanded',
        label: 'Sales Cost Landed',
        sortable: true,
        render: (val) => formatCurrency(val as number),
      },
      {
        id: 'marginLandedPercent',
        label: 'Margin Landed %',
        sortable: true,
        render: (val) => `${val}%`,
      },
    ],
    [],
  );

  const paginationConfig: PaginationConfig | undefined = useMemo(() => {
    if (!shouldFetchData || !data?.pageInfo) return undefined;

    return {
      enabled: true,
      pagination: exportTable.pagination.paginationState,
      onPageSizeChange: exportTable.pagination.setPageSize,
      onPreviousPage: exportTable.pagination.goToPreviousPage,
      onNextPage: exportTable.pagination.goToNextPage,
      onFirstPage: exportTable.pagination.goToFirstPage,
      onLastPage: data.pageInfo.totalPages
        ? () => exportTable.pagination.goToPage(data.pageInfo.totalPages)
        : undefined,
      onGoToPage: exportTable.pagination.goToPage,
      showRowsPerPage: true,
      showPageInfo: true,
      showPageNumbers: true,
      rowsPerPageOptions: [10, 25, 50, 100, 200],
    };
  }, [shouldFetchData, data, exportTable.pagination]);

  const handleFetch = useCallback(() => {
    if (shouldFetchData) {
      refetch();
    } else {
      setShouldFetchData(true);
    }
  }, [shouldFetchData, refetch]);

  const handleDownloadCsv = useCallback(async () => {
    setIsDownloadingCsv(true);
    try {
      await downloadSalesMarginsCsv(apiFilters);
    } catch (error) {
      console.error('Failed to download CSV:', error);
    } finally {
      setIsDownloadingCsv(false);
    }
  }, [apiFilters]);

  const handleDeleteConfirm = useCallback(async () => {
    setIsDeleting(true);
    try {
      const result = await deleteSalesMarginsForYear(selectedYear);
      if (result.success) {
        showToast({
          message: `Deleted ${result.deletedCount} sales margin record(s) for ${selectedYear}`,
          title: 'Success',
          color: 'success',
          icon: 'check',
        });
        // Refresh the data after deletion
        if (shouldFetchData) {
          refetch();
        }
      }
    } catch (error) {
      console.error('Failed to delete sales margins:', error);
      showToast({
        message:
          error instanceof Error
            ? error.message
            : 'Failed to delete sales margins',
        title: 'Error',
        color: 'error',
        icon: 'error',
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  }, [selectedYear, shouldFetchData, refetch, showToast]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sales Margins Export</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground">
                Start Date
              </label>
              <DatePicker
                value={exportTable.filters.dateRange?.from}
                onSelect={(date: Date | undefined) => {
                  exportTable.setDateRange({
                    from: date,
                    to: exportTable.filters.dateRange?.to,
                  });
                }}
                placeholder="Select start date..."
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground">
                End Date
              </label>
              <DatePicker
                value={exportTable.filters.dateRange?.to}
                onSelect={(date: Date | undefined) => {
                  exportTable.setDateRange({
                    from: exportTable.filters.dateRange?.from,
                    to: date,
                  });
                }}
                placeholder="Select end date..."
              />
            </div>
            <MultiSelect
              label="Suppliers"
              options={supplierOptions}
              value={exportTable.filters.suppliers}
              onChange={exportTable.setSuppliers}
              placeholder="Select suppliers..."
              searchPlaceholder="Search suppliers..."
            />
            <MultiSelect
              label="Supplier Tags"
              options={supplierTagOptions}
              value={exportTable.filters.supplierTags}
              onChange={exportTable.setSupplierTags}
              placeholder="Select tags..."
              searchPlaceholder="Search tags..."
            />
            <MultiSelect
              label="Divisions"
              options={divisionOptions}
              value={exportTable.filters.divisions}
              onChange={exportTable.setDivisions}
              placeholder="Select divisions..."
              searchPlaceholder="Search divisions..."
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => setShowDeleteDialog(true)}
              variant="outline"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              Delete {selectedYear} Data
            </Button>
            <Button
              onClick={handleDownloadCsv}
              disabled={isDownloadingCsv || !shouldFetchData}
              variant="outline"
            >
              <DownloadIcon className="h-4 w-4 mr-2" />
              {isDownloadingCsv ? 'Downloading...' : 'Download CSV'}
            </Button>
            <Button onClick={handleFetch} disabled={isLoading}>
              <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
              {isLoading ? 'Loading...' : 'Fetch Data'}
            </Button>
          </div>
        </div>

        {!shouldFetchData ? (
          <div className="text-center py-12 text-muted-foreground">
            Configure filters above and click "Fetch Data" to load results
          </div>
        ) : isLoading || !data?.items ? (
          <TableSkeleton />
        ) : (
          <PaginatedGentlyTable<SalesMarginExportItem>
            data={data.items}
            columns={columns}
            isLoading={isLoading}
            searchPlaceholder="Search sales margins..."
            emptyMessage="No sales margins found"
            showSearch={true}
            showDownload={false}
            downloadFilename="sales-margins"
            showCount={true}
            itemName="sales margins"
            paginationConfig={paginationConfig}
            serverSideSorting={{
              sortBy: exportTable.pagination.sortBy,
              sortDirection:
                exportTable.pagination.sortDirection === 'ASC' ? 'asc' : 'desc',
              onSort: (columnId, direction) => {
                exportTable.pagination.setSorting(
                  columnId,
                  direction === 'asc' ? 'ASC' : 'DESC',
                );
              },
            }}
          />
        )}
      </CardContent>

      <ConfirmDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        title={`Delete ${selectedYear} Sales Margins`}
        message={`Are you sure you want to delete all sales margin data for the year ${selectedYear}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        loading={isDeleting}
        dangerous
      />
    </Card>
  );
};

export default SalesMarginsExportTable;
